import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../axios';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  Divider,
  IconButton
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const addressLabels = {
  buildingNo: 'رقم المبنى',
  streetName: 'اسم الشارع',
  district: 'الحي',
  city: 'المدينة',
  additionalNo: 'الرقم الفرعي',
  postalCode: 'الرمز البريدي'
};

const SectionHeader = ({ title, icon }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1, 
      mb: 2, 
      pb: 1, 
      borderBottom: '2px solid #f0f0f0' 
    }}
  >
    <Box sx={{ color: 'primary.main' }}>{icon}</Box>
    <Typography variant="h6" fontWeight="bold" color="text.primary">
      {title}
    </Typography>
  </Box>
);

export default function CompanyProfileForm() {
  const { companyId: companyIdParam } = useParams();
  const navigate = useNavigate();

  const companyId =
    parseInt(companyIdParam, 10) ||
    parseInt(localStorage.getItem('companyId'), 10) ||
    0;

  const [profile, setProfile] = useState({
    nameAr: '',
    nameEn: '',
    vatNo: '',
    crn: '',
    crType: 'CRN',
    activityAr: '',
    activityEn: '',
    address: '',
    buildingNo: '',
    streetName: '',
    district: '',
    city: '',
    additionalNo: '',
    postalCode: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    enableZatcaInvoice: false,
    logoImage: null
  });
  const [logoPreview, setLogoPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoPreview(dataUrl);
      setProfile((prev) => ({
        ...prev,
        logoImage: dataUrl.split(',')[1] 
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { companyId, ...profile };
      if (isNew) {
        await api.post('/CompanyProfile', payload);
      } else {
        await api.put(`/CompanyProfile/${companyId}`, payload);
      }
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      if (data?.status === 400 && data.errors) {
        const messages = Object.entries(data.errors)
          .map(([f, arr]) => `• ${f}: ${arr.join(', ')}`)
          .join('\n');
        setError(messages);
      } else {
        setError(data?.title || data?.message || 'حدث خطأ أثناء الحفظ.');
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/CompanyProfile/${companyId}`)
      .then((res) => {
        const data = res.data;
        setProfile({ ...data, logoImage: null });
        setLogoPreview(
          data.logoImage ? `data:image/png;base64,${data.logoImage}` : ''
        );
      })
      .catch((err) => {
        if (err.response?.status === 404) setIsNew(true);
        else setError('فشل جلب بيانات المنشأة.');
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) {
    return (
      <Container dir="rtl" sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress size={60} thickness={4} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" dir="rtl" sx={{ py: 4 }}>
      {/* Header */}
      <Box 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
          color: 'white',
          boxShadow: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {isNew ? 'تأسيس منشأة جديدة' : 'بطاقة المنشأة'}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            إدارة البيانات الأساسية، العناوين، وإعدادات الفوترة
          </Typography>
        </Box>
        <BusinessIcon sx={{ fontSize: 60, opacity: 0.2 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} variant="filled">
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          {/* العمود الأيمن: الشعار + المعلومات الأساسية */}
          <Grid item xs={12} lg={4}>
            {/* بطاقة الشعار */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, textAlign: 'center' }}>
              <SectionHeader title="شعار المنشأة" icon={<PhotoCamera />} />
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  src={logoPreview}
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    border: '4px solid #fff', 
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    bgcolor: 'grey.100'
                  }}
                >
                  {!logoPreview && <BusinessIcon sx={{ fontSize: 60, color: 'grey.400' }} />}
                </Avatar>
                <IconButton 
                  color="primary" 
                  aria-label="upload picture" 
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
                  <PhotoCamera />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                اضغط على الكاميرا لتغيير الشعار
              </Typography>
            </Paper>

            {/* إعدادات النظام */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <SectionHeader title="إعدادات النظام" icon={<SettingsIcon />} />
              <Card 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: profile.enableZatcaInvoice ? 'success.light' : 'grey.50',
                  borderColor: profile.enableZatcaInvoice ? 'success.main' : 'grey.300',
                  transition: 'all 0.3s'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="enableZatcaInvoice"
                      checked={profile.enableZatcaInvoice}
                      onChange={handleChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="bold">تفعيل الفاتورة الإلكترونية (ZATCA)</Typography>
                      <Typography variant="caption">تفعيل الربط مع هيئة الزكاة والضريبة والجمارك</Typography>
                    </Box>
                  }
                />
              </Card>
            </Paper>
          </Grid>

          {/* العمود الأيسر: البيانات التفصيلية */}
          <Grid item xs={12} lg={8}>
            {/* المعلومات الأساسية */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <SectionHeader title="المعلومات الأساسية" icon={<BusinessIcon />} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="اسم المنشأة (عربي)"
                    name="nameAr"
                    value={profile.nameAr}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name (English)"
                    name="nameEn"
                    value={profile.nameEn}
                    onChange={handleChange}
                    variant="outlined"
                    dir="ltr"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="الرقم الضريبي (VAT Number)"
                    name="vatNo"
                    value={profile.vatNo}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="رقم السجل التجاري (CRN)"
                    name="crn"
                    value={profile.crn}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="النشاط التجاري"
                    name="activityAr"
                    value={profile.activityAr}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* معلومات الاتصال والعنوان */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 3 }}>
                  <SectionHeader title="معلومات الاتصال" icon={<ContactPhoneIcon />} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="رقم الهاتف"
                        name="phone"
                        value={profile.phone}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="رقم الجوال"
                        name="mobile"
                        value={profile.mobile}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="البريد الإلكتروني"
                        name="email"
                        type="email"
                        value={profile.email}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="الموقع الإلكتروني"
                        name="website"
                        value={profile.website}
                        onChange={handleChange}
                        variant="outlined"
                        dir="ltr"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 3 }}>
                  <SectionHeader title="العنوان الوطني" icon={<LocationOnIcon />} />
                  <Grid container spacing={2}>
                    {Object.keys(addressLabels).map((key) => (
                      <Grid item xs={6} key={key}>
                        <TextField
                          fullWidth
                          label={addressLabels[key]}
                          name={key}
                          value={profile[key] || ''}
                          onChange={handleChange}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="العنوان المختصر"
                        name="address"
                        value={profile.address}
                        onChange={handleChange}
                        variant="outlined"
                        multiline
                        rows={2}
                        placeholder="مثال: الرياض - حي العليا - شارع الملك فهد"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Paper 
          elevation={5} 
          sx={{ 
            position: 'sticky', 
            bottom: 20, 
            mt: 4, 
            p: 2, 
            borderRadius: 4, 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 2,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <Button 
            variant="outlined" 
            color="error" 
            size="large" 
            onClick={() => navigate(-1)}
            startIcon={<CancelIcon />}
            sx={{ borderRadius: 2, px: 4 }}
          >
            إلغاء
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            type="submit" 
            size="large"
            startIcon={<SaveIcon />}
            sx={{ borderRadius: 2, px: 6, boxShadow: 4 }}
          >
            {isNew ? 'إنشاء الملف' : 'حفظ التغييرات'}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
}
